import { useState } from "react";
import "./App.css";
import { useDispatch, useSelector } from "react-redux";
import { toastConfig } from "react-simple-toasts";
import "bootstrap/dist/css/bootstrap.min.css";
import { useEffect } from "react";
import { getAppConfiguration, saveUser } from "./store/actions/auth";
import AuthService from "./services/auth.service";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AppRoutes from "./config/Routes";
import axios from "axios";
import { getUserToken, logout } from "./config/auth";
import { getLocalStorageItem, setLocalStorageItem } from "./services/StorageService";
import { walmartAdapterUrl } from "./constant";
import { BrowserRouter } from "react-router-dom";
import ImpersonationBanner from "./components/common/ImpersonationBanner";
import { forceEndImpersonation, hydrateImpersonationFromStorage } from "./store/actions/impersonation.action";

const App = () => {
  const dispatch = useDispatch();
  const [sidebarOpen, setsidebarOpen] = useState(false);
  const openSidebar = () => {
    setsidebarOpen(true);
  };
  const closeSidebar = () => {
    setsidebarOpen(false);
  };

  toastConfig({
    time: 3000,
    position: "right",
  });

  const { user: currentUser } = useSelector((state) => state.auth);
  const employeePermissions = useSelector((state) => state?.sidebar?.employeePermissions);
  let user = JSON.parse(localStorage.getItem("userDetails"));

  useEffect(() => {
    if (!currentUser && user) {
      dispatch(saveUser(user));
      dispatch(getAppConfiguration());
    }
    dispatch(hydrateImpersonationFromStorage());
  }, []);

  useEffect(() => {
    if (!toast.__readOnlyWrapped) {
      const originalError = toast.error.bind(toast);
      toast.__originalError = originalError;
      toast.error = (...args) => {
        const last = globalThis.__readOnlyToastAt || 0;
        if (Date.now() - last < 1000) return null;
        return originalError(...args);
      };
      toast.__readOnlyWrapped = true;
    }
    const fireReadOnlyToast = (message) => {
      const fn = toast.__originalError || toast.error;
      fn(message);
      globalThis.__readOnlyToastAt = Date.now();
    };

    const reqId = axios.interceptors.request.use((config) => {
      try {
        const raw = localStorage.getItem('impersonation');
        if (!raw) return config;
        const meta = JSON.parse(raw);
        if (!meta?.expiresAt) return config;
        const hasTz = meta.expiresAt.endsWith('Z') || meta.expiresAt.endsWith('z') || /[+-]\d{2}:?\d{2}$/.test(meta.expiresAt);
        const tzAware = hasTz ? meta.expiresAt : `${meta.expiresAt}Z`;
        const expiryTs = new Date(tzAware).getTime();
        if (Number.isNaN(expiryTs) || Date.now() < expiryTs) return config;
        const url = config?.url || '';
        if (url.includes('/api/Impersonation/End')) return config;
        fireReadOnlyToast('Impersonation session expired — action blocked.');
        const expiredError = new Error('Impersonation session expired');
        expiredError.code = 'IMPERSONATION_EXPIRED';
        expiredError.config = config;
        expiredError._impersonationExpired = true;
        return Promise.reject(expiredError);
      } catch {
        return config;
      }
    });

    const id = axios.interceptors.response.use(function (response) {
      const data = response?.data;
      if (data?.Status === 403 && localStorage.getItem('impersonation')) {
        const message = data?.Message?.AppStatusDescription || 'Read-only mode — action not permitted';
        fireReadOnlyToast(message);
        if (data && typeof data === 'object') data._readOnlyBlocked = true;
      }
      return response;
    }, function (error) {
      if (error?._impersonationExpired) return Promise.reject(error);
      const status = error?.response?.status;
      const data = error?.response?.data;
      const isImpersonating = !!localStorage.getItem('impersonation');
      const bodyStatus = data?.Status;
      const isReadOnlyBlock =
        isImpersonating &&
        (status === 403 || bodyStatus === 403) &&
        (data?.error === 'READ_ONLY_IMPERSONATION' ||
          data?.Message?.AppStatusCode === 'RC_ACC_DEN' ||
          data?.Status === 403);
      if (isReadOnlyBlock) {
        const message =
          data?.Message?.AppStatusDescription ||
          data?.message ||
          'Read-only mode — action not permitted';
        fireReadOnlyToast(message);
        if (data && typeof data === 'object') data._readOnlyBlocked = true;
        return Promise.reject(error);
      }
      if (status === 401 && isImpersonating) {
        if (error.config) error.config._retry = true;
        dispatch(forceEndImpersonation());
        toast.info('Impersonation session ended');
        return Promise.reject(error);
      }
      return Promise.reject(error);
    });
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(id);
    };
  }, []);

  const updateToken = (userToken) => {
    let getUserDetails = JSON.parse(getLocalStorageItem('userDetails'));
    getUserDetails.Value.Token = userToken;
    setLocalStorageItem('userDetails', JSON.stringify(getUserDetails));
  }

  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    })

    failedQueue = [];
  }

  axios.interceptors.response.use(function (response) {
    return response
  }, function (error) {
    const originalRequest = error.config;
    if (error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject })
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + getUserToken();
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        })
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise(function (resolve, reject) {
        AuthService.refreshTokenService(getUserToken()).then(({ data }) => {
          updateToken(data?.Value?.Token);
          originalRequest.headers['Authorization'] = 'Bearer ' + data?.Value?.Token;
          processQueue(null, data?.Value?.Token);
          resolve(axios(originalRequest));
        }).catch((err) => {
          processQueue(err, null);
          reject(err);
          logout();
        }).finally(() => { isRefreshing = false })
      })
    }

    return Promise.reject(error);
  });

  axios.interceptors.request.use(async (req) => {
    if (
      (
        !req.url.includes('login') &&
        !req.url.includes('printers') &&
        !req.url.includes('GetStatesByCountry') &&
        !req.url.includes('GetAllCountries') &&
        !req.url.includes('GetZipCode') &&
        !req.url.includes('GetRecaptchaKey') &&
        !req.url.includes('get_all_signup_reason') &&
        !req.url.includes('api/security')
      ) ||
      req.url.includes('login/GenerateTokenByCompanySellerAndUser')
    ) {
      req.headers['Authorization'] = 'Bearer ' + getUserToken();
      req.headers['AccessTokenId'] = 'AccessTokenId';
    }
    return req;
  }, error => {
    return Promise.reject(error);
  });

  axios.interceptors.response.use(response => {
    return response;
  }, error => {
    const isWalmartAdapter = error.config?.url?.includes(walmartAdapterUrl);
    const isImpersonating = !!localStorage.getItem('impersonation');
    if (error.response.status === 401 && !isWalmartAdapter && !isImpersonating) {
      logout();
    }
    return Promise.reject(error);
  });

  return (
    <div className={currentUser ? "containers" : ""}>
      <BrowserRouter>
        <ImpersonationBanner />
        <AppRoutes
          currentUser={currentUser}
          sidebarOpen={sidebarOpen}
          openSidebar={openSidebar}
          closeSidebar={closeSidebar}
          employeePermissions={employeePermissions}
        />
      </BrowserRouter>
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        theme="dark"
      />
    </div>
  );
};

export default App;
